import React, { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, Drawer, message } from "antd";
import styled from "styled-components";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import BrandKindModelSelect from "./brandKindModelSelect";
import { IPkLocalData, IHistoryList } from "@Types/specification";
import { fetchComparedRecommend } from "@Services/specification";
import { useLocalStorageState } from "ahooks";
import { useAtom } from "jotai";
import { pkComparisonAtom, pkListAtom } from "@Lib/jotai/pkCompare";
import { initTracking } from "@Lib/jotai/tracking";

interface IProps {
  onClose: () => void;
  open: boolean;
}

const DrawerStyled = styled(Drawer)`
  .ant-drawer-content-wrapper{
    border-radius: 4px;
    box-shadow: 2px 0px 20px 0px rgba(0,0,0,0.20); 
  }
  .ant-drawer-content{
    border-radius: 4px;
  }
  .ant-drawer-body{
    padding: 0 0 80px 0;
  }
  .ant-drawer-body::-webkit-scrollbar {
    display: none;
  }
`;

const CheckboxStyled = styled(Checkbox)`
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #3264FF;
    border-color: #3264FF;
  }
  .ant-checkbox-inner{
    border-radius: 50%;
    border: 1px solid #bbb;
  }
  .ant-checkbox-checked::after{
    border-radius: 50%;
  }
`;

const HistoryList: React.FC<{ dataSource: IHistoryList[], title: string, handleClickModel: (data: any) => void, pkCheckedList: any }> = (props) => {
  const { dataSource, title, handleClickModel, pkCheckedList } = props;

  return (
    <div className="flex flex-col">
      <p className="bg-[#f5f6fa] h-42 border-0 border-[#EDEFF5] border-b-1 border-t-1 border-solid pl-14 text-[#1D2029] text-14 flex items-center justify-start font-bold">{title}</p>
      {Array.isArray(dataSource) && dataSource.map((item, index) => (
        <div className="mx-20 py-14 flex items-start border-0 border-[#EDEFF5] border-b-1 border-solid relative" key={index}>
          <div className="flex flex-col items-start justify-start text-14 leading-20">
            <div className="text-[#1D2029] w-236 mb-12">{item.brandName} {item.kindName} {item.year + '年式'} {item.modelName}</div>
            <div className={`${!!item.price ? 'text-[#F87F15] font-[600]' : 'text-[#656873]'}`}>{item.price}</div>
          </div>

          <div
            className={`absolute right-0 bottom-14 w-64 h-24 rounded-4 flex items-center justify-center cursor-pointer ${pkCheckedList.has(item!.myid) ? 'bg-[#F3F4F8] text-[#9DA2AF]' : 'bg-[#3264FF] text-[#fff]'}`}
            onClick={() => handleClickModel(item)}
            id="tracking-CommonHistoryAddToPK"
          >
            {!pkCheckedList.has(item!.myid) && <img src="https://photo.8891.com.tw/2024/11/22/1732239634722255.png" width={8} height={8} alt="" className="mr-2" />}
            {pkCheckedList.has(item!.myid) ? '已加入' : '比較'}
          </div>
        </div>
      ))}
    </div>
  );
};


const AddToPK: React.FC<IProps> = ({ onClose, open }) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [pkDataList, setPkDataList] = useLocalStorageState<IPkLocalData[]>(`globalPkComparison`);
  const [historyData, setHistoryData] = useState<IHistoryList[]>([]);
  const [globalPkList, updateGlobalPkList] = useAtom(pkComparisonAtom);
  const [, initGlobalPkList] = useAtom(pkListAtom);
  const [tracking] = useAtom(initTracking);

  const clearAll = () => {
    setPkDataList([]);
    localStorage.removeItem(`globalPkComparison`);
    initGlobalPkList([])
  }

  const handleCloseClick = (myid: number) => {
    if (pkDataList && !!pkDataList.length) {
      const index = pkDataList.findIndex(item => item.myid === myid);
      if (index !== -1) {
        updateGlobalPkList({
          data: pkDataList[index],
          callback: (data) => {
            setPkDataList(data)
          }
        });
      }
    }
  }

  // 獲取、最近浏览
  const getHistoryList = async () => {
    try {
      const res: { data, status } = await fetchComparedRecommend({
        limit: 3
      });
      if (res.status === 12000) {
        setHistoryData(res.data.list || []);
      }
    } catch (error) {
      console.log(error);
    }
  }

  // 加入比较
  const setAddCompeteList = (data) => {
    updateGlobalPkList({
      data,
      callback: (data) => {
        setPkDataList(data)
      }
    });

    tracking && tracking.trackEvent({
      name: "element_clicked",
      params: {
        "page_business": "新車",
        "trigger_id": "4032",
        "page_id": "714",
        "trigger_name": "最近瀏覽+比較",
        "object_type": "myid",
        "next_spm": "3.714.809.2214",
        "page": "新車添加比較彈窗頁",
        "channel": "新車",
        "element_id": "最近瀏覽+比較",
        "element": "按鈕",
        "module": "添加比較彈窗",
        "tracker_method": "config",
        object_id: data.myid || '',
      }
    });
  };

  // 点击外部关闭
  const handleClickOutside = (event) => {
    if (drawerRef.current && !(drawerRef.current as HTMLElement).contains(event.target as HTMLElement) && event.target.id !== 'AddToPKBtn') {
      onClose?.();
    }
  };

  // 点击checkbox
  const handleCheckedChange = (e, item) => {
    const isChecked = e.target.checked;
    const checkedCount = (pkDataList || []).filter(item => item.isChecked).length;
    if (checkedCount >= 10 && isChecked) {
      message.error('最多可對比10個車型，請刪除一個再添加；');
      return;
    } else {
      const index = (pkDataList || []).findIndex(i => i.myid === item.myid);
      if (index !== -1) {
        const updatedList = [...pkDataList!];
        updatedList[index].isChecked = isChecked;
        setPkDataList(updatedList);
      }
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // table组件更新后监听
  useEffect(() => {
    getHistoryList();
  }, []);

  const isPkCheckedMap = useMemo(() => {
    const checkedList = pkDataList?.filter(item => item.isChecked).map(item => item.myid) || [];
    return new Set(checkedList);
  }, [pkDataList]);

  const isPkListMap = useMemo(() => {
    const checkedList = pkDataList?.map(item => item.myid) || [];
    return new Set(checkedList);
  }, [pkDataList]);


  useEffect(() => {
    setPkDataList(globalPkList)
  }, [globalPkList])

  return (
    <DrawerStyled
      bodyStyle={{
        overflowY: "scroll",
        scrollbarWidth: "none",
      }}
      width={354}
      placement="right"
      mask={false}
      closable={false}
      onClose={onClose}
      open={open}
    >
      <div ref={drawerRef} id="tracking-CommonAddToPk">
        <div className="w-full h-52 pl-20 text-[#1D2029] text-20 font-[600] flex items-center justify-start" style={{ background: 'linear-gradient(0deg,rgba(97,157,255,0.00), rgba(50,100,255,0.20))' }}>
          車型
          <span className="text-[#3264FF] italic ml-4">P</span>
          <span className="text-[#F87F15] italic">K</span>
        </div>
        <div className="text-[#1D2029] text-14 leading-20 pl-20">最多可對比<span className="text-[#F87F15]">10</span>款車型</div>
        <div className="max-h-[485px] overflow-y-scroll scroll-hover-scrollbar">
          {Array.isArray(pkDataList) && !!pkDataList.length && pkDataList.map((item, index) => (
            <div className="mx-20 py-14 flex items-center border-0 border-[#EDEFF5] border-b-1 border-solid relative" key={index}>
              <CheckboxStyled checked={isPkCheckedMap.has(item.myid)} onChange={(e) => handleCheckedChange(e, item)} />
              <img src={item.thumb || 'https://photo.8891.com.tw/2024/12/16/1734328175772587.png'} alt="" className="w-90 h-60 mr-14 object-cover ml-8" />
              <div className="flex flex-col items-start justify-start text-14 leading-20">
                <div className="text-[#1D2029] w-168 mb-8 pr-14">{item.brandName} {item.kindName} {!!item.year && (String(item.year).includes('年式') ? item.year : `${item.year}年式`)} {item.modelName}</div>
                <div className={`${!!item.price && item.price === '暫無報價' ? 'text-[#656873]' : 'text-[#F87F15] font-[600]'}`}>{!!item.price && (item.price === '暫無報價' ? '暫無報價' : String(item.price).includes('萬') ? item.price : `${item.price}萬`)}</div>
              </div>

              <CloseOutlined className="absolute right-0 top-0 bottom-0 my-auto cursor-pointer w-12 h-12" style={{ color: '#9DA2AF' }} onClick={() => handleCloseClick(item.myid)} />
            </div>
          ))}
        </div>

        <BrandKindModelSelect disabledModels={pkDataList?.map(item => item.myid)} title={'添加對比車型'} SelectType="add" />

        <a
          target='_blank'
          href={
            !!isPkCheckedMap?.size && isPkCheckedMap.size > 1
              ? `/photoModel-compare.html?myids=${Array.from(isPkCheckedMap).join(",")}&t=tc`
              : "###"
          }
          className={`h-32 flex items-center justify-center text-14 rounded-4 mt-14 mx-20 border-1 border-solid ${pkDataList && !!pkDataList.length && isPkCheckedMap!.size > 1 ? 'text-[#fff] hover:text-[#fff] bg-[#3264FF] border-[#3264FF]' : 'text-[#939BAF] hover:text-[#939BAF] bg-[#F5F6FA] border-[#EDEFF5] cursor-default'}`}
          onClick={(e) => {
            ((pkDataList && !!pkDataList.length && isPkCheckedMap?.size === 1) || !isPkCheckedMap?.size) && e.preventDefault();

            isPkCheckedMap.size > 1 && tracking && tracking.trackEvent({
              name: "element_clicked",
              params: {
                page_business: "新車",
                trigger_id: "4030",
                page_id: "714",
                trigger_name: "開始比較",
                object_type: "myid",
                next_spm: "3.714.809.2213",
                page: "新車添加比較彈窗頁",
                channel: "新車",
                element_id: "開始比較",
                element: "按鈕",
                module: "添加比較彈窗",
                tracker_method: "config",
                object_id: Array.from(isPkCheckedMap).join(",") || '',
              }
            });
          }}
          id="tracking-CommonStartComparing"
        >開始比較({isPkCheckedMap.size})</a>

        <div className="flex items-center justify-center text-14 leading-20 text-[#656873] mt-14 mb-24 cursor-pointer" onClick={() => clearAll()}>
          <img src="https://photo.8891.com.tw/2024/11/11/1731311476724103.png" alt="" className="w-14 h-14 mr-6" />
          清除所有車型
        </div>

        {Array.isArray(historyData) && !!historyData.length &&
          <HistoryList dataSource={historyData} handleClickModel={setAddCompeteList} title="最近瀏覽" pkCheckedList={isPkListMap} />
        }
      </div>
    </DrawerStyled >


  );
};
export default AddToPK;
